import { z } from 'zod';
import type { ProfileRow } from '@/db/schema';

/**
 * ApplicantAnswersSchema
 * @description The data-driven source of truth for every fact an ATS handler needs to fill a job application form. Modeled on the canonical field set from andrew-scripts/lib/applicant.ts, but kept generic so any applicant profile can drive it. Nearly everything is optional with sensible defaults so a partial profile still parses cleanly.
 * ATS handler needs to fill a job application form. Modeled on the canonical field set from andrew-scripts/lib/applicant.ts, but kept generic so any applicant profile can drive it. Nearly everything is optional with sensible defaults so a partial profile still parses cleanly.
 */

/**
 * AddressSchema
 * @description The address schema.
 */
const AddressSchema = z.object({
  city: z.string().optional(),
  country: z.string().optional(),
  line1: z.string().optional(),
  postalCode: z.string().optional(),
  state: z.string().optional(),
  stateCode: z.string().optional(),
});

/**
 * LinksSchema
 * @description The links schema.
 */
const LinksSchema = z.object({
  github: z.string().optional(),
  linkedin: z.string().optional(),
  website: z.string().optional(),
});

/**
 * EeoSchema
 * @description The EEO schema.
 */
const EeoSchema = z.object({
  disabilityStatus: z.string().optional(),
  gender: z.string().optional(),
  genderIdentity: z.string().optional(),
  hispanicLatino: z.string().optional(),
  race: z.string().optional(),
  veteranStatus: z.string().optional(),
});

/**
 * CollegeSchema
 * @description The college schema.
 */
const CollegeSchema = z.object({
  cityState: z.string().optional(),
  degree: z.string().optional(),
  graduated: z.boolean().optional(),
  major: z.string().optional(),
  name: z.string().optional(),
  yearsCompleted: z.string().optional(),
});

/**
 * HighSchoolSchema
 * @description The high school schema.
 */
const HighSchoolSchema = z.object({
  cityState: z.string().optional(),
  graduated: z.boolean().optional(),
  lastGrade: z.string().optional(),
  name: z.string().optional(),
});

/**
 * EducationSchema
 * @description The education schema.
 */
const EducationSchema = z.object({
  college: CollegeSchema.optional(),
  highestEducation: z.string().optional(),
  highSchool: HighSchoolSchema.optional(),
});

/**
 * EmploymentEntrySchema
 * @description The employment entry schema.
 */
export const EmploymentEntrySchema = z.object({
  company: z.string().optional(),
  current: z.boolean().optional(),
  from: z.string().optional(),
  location: z.string().optional(),
  okToContact: z.boolean().optional(),
  reasonForLeaving: z.string().optional(),
  responsibilities: z.string().optional(),
  title: z.string().optional(),
  to: z.string().optional(),
});

/**
 * ReferenceSchema
 * @description The reference schema.
 */
const ReferenceSchema = z.object({
  company: z.string().optional(),
  email: z.string().optional(),
  knownSinceYear: z.number().optional(),
  name: z.string().optional(),
  phone: z.string().optional(),
  type: z.string().optional(),
});

/**
 * ApplicantAnswersSchema
 * @description The applicant answers schema.
 */
export const ApplicantAnswersSchema = z.object({
  address: AddressSchema.default({ country: 'United States of America' }),
  availableStartDateChoice: z.enum(['today', 'twoWeeks']).default('twoWeeks'),
  countryPhoneCode: z.string().default('+1'),
  customAnswers: z.record(z.string(), z.string()).default({}),
  dateOfBirth: z.string().optional(),
  desiredCompensation: z.string().optional(),
  education: EducationSchema.default({}),
  eeo: EeoSchema.default({
    disabilityStatus: "No, I don't have a disability",
    hispanicLatino: 'No',
    veteranStatus: 'I am not a protected veteran',
  }),
  email: z.string().optional(),
  employmentHistory: z.array(EmploymentEntrySchema).default([]),
  firstName: z.string().optional(),
  howHeardAboutUs: z.string().default('LinkedIn'),
  lastName: z.string().optional(),
  links: LinksSchema.default({}),
  name: z.string().optional(),
  needsVisaSponsorship: z.boolean().default(false),
  phone: z.string().optional(),
  references: z.array(ReferenceSchema).default([]),
  smsConsent: z.boolean().default(true),
  workAuthorized: z.boolean().default(true),
  yearsExperience: z.string().default('4+'),
});

/**
 * ApplicantAnswers
 * @description The applicant answers type.
 */
export type ApplicantAnswers = z.infer<typeof ApplicantAnswersSchema>;

export type AvailableStartDateChoice =
  ApplicantAnswers['availableStartDateChoice'];

/**
 * parseApplicantAnswers
 * @param raw - The raw answers.
 * @returns ApplicantAnswers
 * @description JSON-parse + schema-parse a stored answers blob. Returns schema defaults (an empty-but-valid object) on null/invalid input so callers never have to guard against a missing or corrupt profile column.
 */
export const parseApplicantAnswers = (raw: string | null) => {
  if (!raw) {
    return ApplicantAnswersSchema.parse({});
  }

  try {
    const parsed = ApplicantAnswersSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : ApplicantAnswersSchema.parse({});
  } catch {
    return ApplicantAnswersSchema.parse({});
  }
};

/**
 * seedAnswersFromProfile
 * @param profile - The profile.
 * @returns ApplicantAnswers
 * @description Pre-fill the answers a profile row already knows about (name, email, location) so onboarding starts from a sensible baseline rather than an empty form. location_preference maps to address.line1 as a free-text hint; downstream handlers can refine it.
 */
export const seedAnswersFromProfile = (
  profile: Pick<ProfileRow, 'name' | 'location_preference'> & {
    email?: string | null;
  },
) => {
  const [firstName, ...rest] = (profile.name ?? '').trim().split(/\s+/);

  return ApplicantAnswersSchema.parse({
    address: profile.location_preference
      ? { line1: profile.location_preference }
      : {},
    email: profile.email ?? undefined,
    firstName: firstName || undefined,
    lastName: rest.length > 0 ? rest.join(' ') : undefined,
    name: profile.name || undefined,
  });
};
